import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { roleActionErrorToast, supportTicketSuccessToast } from '@/lib/user-feedback';
import {
  CATEGORY_LABELS,
  STATUS_LABELS,
  type SupportTicket,
  type SupportTicketCategory,
  type SupportTicketStatus,
} from './types';

interface EnrolledCourse {
  id: string;
  title: string;
  name?: string;
}

type StatusFilter = 'all' | 'active' | 'resolved';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function studentTicketStatusClass(status: SupportTicketStatus): string {
  if (status === 'resuelto') return 'support-student-status support-student-status--resolved';
  if (status === 'en_progreso') return 'support-student-status support-student-status--progress';
  if (status === 'abierto') return 'support-student-status support-student-status--open';
  return 'support-student-status support-student-status--closed';
}

function matchesStatusFilter(ticket: SupportTicket, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'active') return ticket.status === 'abierto' || ticket.status === 'en_progreso';
  return ticket.status === 'resuelto' || ticket.status === 'cerrado';
}

function matchesSearch(ticket: SupportTicket, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    ticket.subject,
    ticket.categoryLabel,
    ticket.course?.name ?? '',
    String(ticket.number),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

export function MisSoportePage() {
  const { hasPermission } = usePermissions();
  const isStaffSupport = hasPermission(PERMISSIONS.SOPORTE_VER);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<SupportTicketCategory>('tecnico');
  const [courseId, setCourseId] = useState('');
  const [body, setBody] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['student', 'support', 'tickets'],
    queryFn: () => api<{ data: SupportTicket[] }>('/student/support/tickets'),
    enabled: !isStaffSupport,
  });

  const { data: coursesData } = useQuery({
    queryKey: ['student', 'courses'],
    queryFn: () => api<{ data: EnrolledCourse[] }>('/student/courses'),
    enabled: !isStaffSupport && showCreate && category === 'curso',
  });

  useEffect(() => {
    if (searchParams.get('nuevo') === '1') {
      setShowCreate(true);
    }
  }, [searchParams]);

  const createMutation = useMutation({
    mutationFn: (payload: {
      subject: string;
      category: SupportTicketCategory;
      body: string;
      courseId?: string;
    }) => api<SupportTicket>('/student/support/tickets', { method: 'POST', body: payload }),
    onSuccess: async (ticket) => {
      toast(supportTicketSuccessToast());
      queryClient.setQueryData(['student', 'support', 'tickets', ticket.id], ticket);
      await queryClient.invalidateQueries({ queryKey: ['student', 'support', 'tickets'] });
      navigate(`/soporte/${ticket.id}`);
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo crear el ticket'));
    },
  });

  if (isStaffSupport) {
    return <Navigate to="/soporte/admin" replace />;
  }

  const courses = coursesData?.data ?? [];

  const rows = useMemo(() => {
    return [...(data?.data ?? [])]
      .filter((ticket) => matchesStatusFilter(ticket, statusFilter))
      .filter((ticket) => matchesSearch(ticket, searchQuery))
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [data?.data, searchQuery, statusFilter]);

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    createMutation.mutate({
      subject,
      category,
      body,
      courseId: category === 'curso' ? courseId : undefined,
    });
  }

  function openCreateForm() {
    setShowCreate(true);
    setSearchParams({ nuevo: '1' }, { replace: true });
  }

  function closeCreateForm() {
    setShowCreate(false);
    setSearchParams({}, { replace: true });
  }

  return (
    <div className="lms-support-page lms-site-admin-hub-page font-sans">
        <header className="support-student-header">
          <div className="support-student-header__copy">
            {showCreate ? (
              <div className="support-student-header__title-row">
                <HubBackLink onBack={closeCreateForm} label="Volver a mesa de ayuda" />
                <h1 className="dash-serif text-3xl font-bold text-dash-primary md:text-4xl">
                  Nueva solicitud
                </h1>
              </div>
            ) : (
              <>
                <h1 className="dash-serif text-3xl font-bold text-dash-primary md:text-4xl">
                  Mesa de ayuda
                </h1>
                <p className="support-student-header__subtitle">
                  Crea solicitudes de soporte y sigue el estado de tus tickets.
                </p>
              </>
            )}
          </div>
          {!showCreate && (
            <div className="support-student-header__action">
              <button type="button" className="support-student-create-btn" onClick={openCreateForm}>
                <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                  add
                </span>
                Nueva solicitud
              </button>
            </div>
          )}
        </header>

        {showCreate && (
          <form onSubmit={handleCreate} className="support-student-create-form">
            <div className="support-student-create-form__grid">
              <label className="support-student-field support-student-field--full">
                <span className="support-student-field__label">Asunto</span>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="support-student-input"
                  required
                />
              </label>
              <label className="support-student-field support-student-field--full">
                <span className="support-student-field__label">Categoría</span>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as SupportTicketCategory)}
                  className="support-student-select"
                >
                  {(Object.keys(CATEGORY_LABELS) as SupportTicketCategory[]).map((key) => (
                    <option key={key} value={key}>
                      {CATEGORY_LABELS[key]}
                    </option>
                  ))}
                </select>
              </label>
              {category === 'curso' && (
                <label className="support-student-field support-student-field--full">
                  <span className="support-student-field__label">Curso</span>
                  <select
                    value={courseId}
                    onChange={(e) => setCourseId(e.target.value)}
                    className="support-student-select"
                    required
                  >
                    <option value="">Selecciona un curso</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title || course.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="support-student-field support-student-field--full">
                <span className="support-student-field__label">Mensaje</span>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  className="support-student-textarea"
                  required
                />
              </label>
            </div>
            <div className="support-student-create-form__actions">
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="support-student-create-btn"
              >
                Enviar solicitud
              </button>
              <button
                type="button"
                className="support-student-cancel-btn"
                onClick={closeCreateForm}
              >
                Cancelar
              </button>
            </div>
          </form>
        )}

        {!showCreate && (
          <>
            <section className="support-student-controls" aria-label="Filtros y búsqueda">
              <nav className="support-student-tabs" aria-label="Pestañas de estado">
                <button
                  type="button"
                  className={`support-student-tab${statusFilter === 'all' ? ' support-student-tab--active' : ''}`}
                  onClick={() => setStatusFilter('all')}
                >
                  Todos
                </button>
                <button
                  type="button"
                  className={`support-student-tab${statusFilter === 'active' ? ' support-student-tab--active' : ''}`}
                  onClick={() => setStatusFilter('active')}
                >
                  Abiertos / En progreso
                </button>
                <button
                  type="button"
                  className={`support-student-tab${statusFilter === 'resolved' ? ' support-student-tab--active' : ''}`}
                  onClick={() => setStatusFilter('resolved')}
                >
                  Resueltos
                </button>
              </nav>
              <div className="support-student-search">
                <span className="support-student-search__icon" aria-hidden="true">
                  <span className="material-symbols-outlined text-[18px]">search</span>
                </span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="support-student-search__input"
                  placeholder="Buscar tickets..."
                  aria-label="Buscar tickets"
                />
              </div>
            </section>

            <section className="support-student-list" aria-label="Lista de tickets de soporte">
              {isLoading ? (
                <div className="support-student-loading">
                  <Loader label="Cargando tickets…" />
                </div>
              ) : rows.length === 0 ? (
                <div className="support-student-empty">
                  <span className="material-symbols-outlined support-student-empty__icon" aria-hidden="true">
                    support_agent
                  </span>
                  <p>No tienes solicitudes de soporte todavía.</p>
                </div>
              ) : (
                rows.map((row) => (
                  <article key={row.id} className="support-student-card">
                    <div className="support-student-card__main">
                      <div className="support-student-card__badges">
                        <span className="support-student-card__number">#{row.number}</span>
                        <span className={studentTicketStatusClass(row.status)}>
                          <span className="support-student-status__dot" aria-hidden="true" />
                          {STATUS_LABELS[row.status]}
                        </span>
                      </div>
                      <h2 className="support-student-card__subject">
                        <Link to={`/soporte/${row.id}`}>{row.subject}</Link>
                      </h2>
                      <div className="support-student-card__meta">
                        <span>{row.categoryLabel}</span>
                        {row.course ? (
                          <>
                            <span className="support-student-card__sep" aria-hidden="true">
                              ·
                            </span>
                            <span>{row.course.name}</span>
                          </>
                        ) : null}
                        <span className="support-student-card__sep" aria-hidden="true">
                          ·
                        </span>
                        <span className="support-student-card__updated">
                          <span className="material-symbols-outlined text-[15px]" aria-hidden="true">
                            schedule
                          </span>
                          Actualizado {formatDate(row.updatedAt)}
                        </span>
                      </div>
                    </div>
                    <div className="support-student-card__action">
                      <Link to={`/soporte/${row.id}`} className="support-student-card__link">
                        <span>Ver ticket</span>
                        <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
                          arrow_forward
                        </span>
                      </Link>
                    </div>
                  </article>
                ))
              )}
            </section>
          </>
        )}
    </div>
  );
}
